import React, { useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";

import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import BrandSwitch from "@/common/Switch/BrandSwitch";
import Text from "@/common/Text/Text";

import { Form, Input, TimePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";

import ControlPanelActionRow from "../ControlPanelActionRow/ControlPanelActionRow";

import {
  REPORT_KK_MUSIC_SCHEDULE_COMMAND,
  UPDATE_KK_MUSIC_SCHEDULE_COMMAND,
} from "@/features/ui/constants";
import {
  useLazyGetDeviceCommandStatusQuery,
  useSendDeviceCommandMutation,
} from "@/features/devices/devicesApi";
import type { MusicScheduleCommandBody } from "@/features/devices/devicesApi";
import type { DeviceDetailResponse } from "@/features/devices/deviceDetailDisplay";
import {
  DEVICE_COMMAND_OPERATOR_COPY,
  waitForDeviceCommandOperatorResult,
} from "@/features/devices/waitForDeviceCommandOperatorResult";
import { REMOTE_SETTINGS_RETRY_LABEL } from "@/features/devices/remoteSettingsOnOpenRefresh";
import {
  CHANNEL_ID_MAX_LENGTH,
  CHANNEL_NAME_MAX_LENGTH,
  MUSIC_SCHEDULE_COPY,
  buildMusicScheduleCommandBody,
  canConfirmMusicSchedule,
  confirmedMusicScheduleFromDetail,
  decideMusicScheduleOpenRefresh,
  decidePostMusicConfirmFailure,
  draftFromConfirmed,
  pendingMusicScheduleCopy,
  reportKkMusicScheduleRequest,
  type ConfirmedMusicSchedule,
  type MusicScheduleDraft,
  type MusicScheduleErrors,
  type SlotKey,
} from "@/features/devices/musicSchedule";
import styles from "./SettingsForm.module.scss";

type RefetchDetail = () => PromiseLike<{
  data?: DeviceDetailResponse | undefined;
}>;

interface Props {
  deviceId: string;
  commandsEnabled: boolean;
  disabled?: boolean;
  /** Parent Remote Console busy (app-meta Confirm / read in flight). */
  externalBusy: boolean;
  data: DeviceDetailResponse | undefined;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: RefetchDetail;
  /** Lets the parent lock its own Confirm while a schedule command is in flight. */
  onBusyChange?: (busy: boolean) => void;
}

interface MusicFormValues {
  enabled: boolean;
  slot1ChannelId: string;
  slot1ChannelName: string;
  slot1Time: Dayjs | null;
  slot2ChannelId: string;
  slot2ChannelName: string;
  slot2Time: Dayjs | null;
}

type Phase =
  | "idle"
  | "loading"
  | "ready"
  | "sending"
  | "unsupported"
  | "unavailable"
  | "not-reported";

type MusicFieldName = keyof MusicFormValues;

const SLOTS: { key: SlotKey; prefix: "slot1" | "slot2"; label: string }[] = [
  { key: "slot1", prefix: "slot1", label: "Slot 1" },
  { key: "slot2", prefix: "slot2", label: "Slot 2" },
];

function valuesFromConfirmed(c: ConfirmedMusicSchedule): MusicFormValues {
  const d = draftFromConfirmed(c);
  return {
    enabled: d.enabled,
    slot1ChannelId: d.slot1.channelId,
    slot1ChannelName: d.slot1.channelName,
    slot1Time: d.slot1.time ? dayjs(d.slot1.time, "HH:mm") : null,
    slot2ChannelId: d.slot2.channelId,
    slot2ChannelName: d.slot2.channelName,
    slot2Time: d.slot2.time ? dayjs(d.slot2.time, "HH:mm") : null,
  };
}

function draftFromValues(v: MusicFormValues): MusicScheduleDraft {
  return {
    enabled: v.enabled === true,
    slot1: {
      channelId: v.slot1ChannelId ?? "",
      channelName: v.slot1ChannelName ?? "",
      time: v.slot1Time ? v.slot1Time.format("HH:mm") : null,
    },
    slot2: {
      channelId: v.slot2ChannelId ?? "",
      channelName: v.slot2ChannelName ?? "",
      time: v.slot2Time ? v.slot2Time.format("HH:mm") : null,
    },
  };
}

/**
 * T24 — Music Scheduler (KrossKast App). Sends update_kk_music_schedule via
 * Fleet only on Confirm; sends report_kk_music_schedule on open when Fleet
 * advertises support. Hydrates from device-CONFIRMED state only and never
 * silently retries update_kk_music_schedule.
 */
export const MusicSchedulerSection: React.FC<Props> = ({
  deviceId,
  commandsEnabled,
  disabled,
  externalBusy,
  data,
  isFetching,
  isError,
  isSuccess,
  refetch,
  onBusyChange,
}) => {
  const [form] = Form.useForm<MusicFormValues>();
  const [phase, setPhase] = useState<Phase>("idle");
  const [fieldIsTouched, setFieldIsTouched] = useState(false);
  const [confirmed, setConfirmed] = useState<ConfirmedMusicSchedule | null>(null);
  const [pending, setPending] = useState<MusicScheduleCommandBody | null>(null);
  const [lastResult, setLastResult] = useState<"successful" | "unsuccessful" | null>(
    null
  );
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const reportStartedRef = useRef(false);
  const generationRef = useRef(0);
  const confirmedAtConfirmRef = useRef<ConfirmedMusicSchedule | null>(null);

  const [sendDeviceCommand] = useSendDeviceCommandMutation();
  const [getCommandStatus] = useLazyGetDeviceCommandStatusQuery();

  const isMutating = phase === "sending" || phase === "loading";
  useEffect(() => {
    onBusyChange?.(isMutating);
  }, [isMutating, onBusyChange]);

  const controlsLocked = Boolean(
    disabled || externalBusy || isMutating || phase !== "ready"
  );

  const applyConfirmed = useCallback(
    (snap: ConfirmedMusicSchedule) => {
      form.setFieldsValue(valuesFromConfirmed(snap));
      const clearable: MusicFieldName[] = [
        "slot1ChannelId",
        "slot1ChannelName",
        "slot1Time",
        "slot2ChannelId",
        "slot2ChannelName",
        "slot2Time",
      ];
      form.setFields(clearable.map((name) => ({ name, errors: [] })));
      setConfirmed(snap);
      setFieldIsTouched(false);
      setFormError(null);
      setPhase("ready");
    },
    [form]
  );

  const pollCommand = useCallback(
    (command: string, commandId?: string) =>
      waitForDeviceCommandOperatorResult({
        commandId,
        command,
        getStatus: async (id) => {
          const res = await getCommandStatus({ commandId: id }).unwrap();
          return res.command;
        },
      }),
    [getCommandStatus]
  );

  // Reset on device change.
  useEffect(() => {
    reportStartedRef.current = false;
    confirmedAtConfirmRef.current = null;
    setPhase("idle");
    setConfirmed(null);
    setPending(null);
    setLastResult(null);
    setStatusOverride(null);
    setFormError(null);
    setFieldIsTouched(false);
    generationRef.current += 1;
    return () => {
      generationRef.current += 1;
    };
  }, [deviceId, commandsEnabled]);

  // On open: report_kk_music_schedule (read only), only when Fleet advertises support.
  useEffect(() => {
    const decision = decideMusicScheduleOpenRefresh({
      deviceId,
      commandsEnabled,
      reportStarted: reportStartedRef.current,
      isFetching,
      isError,
      isSuccess,
      commandSupport: data?.commandSupport,
    });
    if (decision.type === "wait" || decision.type === "skip") return;
    reportStartedRef.current = true;
    if (decision.type === "unavailable") {
      setPhase("unavailable");
      return;
    }
    if (decision.type === "unsupported") {
      setPhase("unsupported");
      return;
    }
    const startedAt = ++generationRef.current;
    const isCurrent = () => startedAt === generationRef.current;
    setPhase("loading");
    setStatusOverride(null);
    void (async () => {
      try {
        const sent = await sendDeviceCommand(
          reportKkMusicScheduleRequest(deviceId)
        ).unwrap();
        if (!isCurrent()) return;
        const state = await pollCommand(
          REPORT_KK_MUSIC_SCHEDULE_COMMAND,
          sent.commandId
        );
        if (!isCurrent()) return;
        if (state !== "successful") {
          setPhase("unavailable");
          return;
        }
        const again = await refetch();
        if (!isCurrent()) return;
        const next = confirmedMusicScheduleFromDetail(again.data?.detail?.player);
        if (next) applyConfirmed(next);
        else setPhase("not-reported");
      } catch {
        if (!isCurrent()) return;
        setPhase("unavailable");
      }
    })();
  }, [
    applyConfirmed,
    commandsEnabled,
    data,
    deviceId,
    isError,
    isFetching,
    isSuccess,
    pollCommand,
    refetch,
    retryNonce,
    sendDeviceCommand,
  ]);

  const handleRetry = () => {
    generationRef.current += 1;
    reportStartedRef.current = false;
    setConfirmed(null);
    setPending(null);
    setLastResult(null);
    setStatusOverride(null);
    setPhase("idle");
    setRetryNonce((n) => n + 1);
  };

  /** Failure/timeout/exception: reconcile to Fleet confirmed. Never resends. */
  const reconcileAfterFailedConfirm = useCallback(async () => {
    setPending(null);
    let next: ConfirmedMusicSchedule | null = null;
    let reachable = false;
    try {
      const again = await refetch();
      reachable = true;
      next = confirmedMusicScheduleFromDetail(again.data?.detail?.player);
    } catch {
      reachable = false;
    }
    const action = decidePostMusicConfirmFailure({
      fleetReachable: reachable,
      confirmed: next,
    });
    setLastResult("unsuccessful");
    if (action.type === "apply-confirmed" && next) {
      setStatusOverride(null);
      applyConfirmed(next);
      return;
    }
    setFieldIsTouched(false);
    setConfirmed(null);
    setStatusOverride(MUSIC_SCHEDULE_COPY.confirmationUnavailable);
    setPhase("unavailable");
  }, [applyConfirmed, refetch]);

  const handleSubmit = async (values: MusicFormValues) => {
    if (!commandsEnabled || phase !== "ready" || !confirmed) return;
    if (externalBusy || isMutating) return;
    const built = buildMusicScheduleCommandBody(draftFromValues(values), confirmed);
    if (!built.ok) {
      const errs: MusicScheduleErrors = built.errors;
      setFormError(errs.form ?? null);
      const fields: { name: MusicFieldName; errors: string[] }[] = [];
      SLOTS.forEach(({ key, prefix }) => {
        const e = errs[key];
        if (!e) return;
        if (e.channelId) fields.push({ name: `${prefix}ChannelId` as MusicFieldName, errors: [e.channelId] });
        if (e.channelName) fields.push({ name: `${prefix}ChannelName` as MusicFieldName, errors: [e.channelName] });
        if (e.time) fields.push({ name: `${prefix}Time` as MusicFieldName, errors: [e.time] });
      });
      form.setFields(fields);
      return;
    }
    setFormError(null);
    confirmedAtConfirmRef.current = confirmed;
    setPending(built.body);
    setLastResult(null);
    setStatusOverride(null);
    setPhase("sending");
    try {
      const sent = await sendDeviceCommand({
        deviceId,
        command: UPDATE_KK_MUSIC_SCHEDULE_COMMAND,
        musicSchedule: built.body,
      }).unwrap();
      const state = await pollCommand(UPDATE_KK_MUSIC_SCHEDULE_COMMAND, sent.commandId);
      if (state === "successful") {
        setPending(null);
        const again = await refetch();
        const next = confirmedMusicScheduleFromDetail(again.data?.detail?.player);
        if (next) {
          setLastResult("successful");
          applyConfirmed(next);
        } else {
          // ACK succeeded but Fleet has no confirmed snapshot — draft is not applied.
          setFieldIsTouched(false);
          setConfirmed(null);
          setStatusOverride(MUSIC_SCHEDULE_COPY.confirmationUnavailable);
          setPhase("not-reported");
        }
      } else {
        await reconcileAfterFailedConfirm();
      }
    } catch {
      await reconcileAfterFailedConfirm();
    }
  };

  const onValuesChange = () => {
    setFieldIsTouched(form.isFieldsTouched());
    setFormError(null);
  };

  let statusText = "";
  if (statusOverride) statusText = statusOverride;
  else if (phase === "loading") statusText = MUSIC_SCHEDULE_COPY.reading;
  else if (phase === "unsupported") statusText = MUSIC_SCHEDULE_COPY.unsupported;
  else if (phase === "unavailable") statusText = MUSIC_SCHEDULE_COPY.unavailable;
  else if (phase === "not-reported") statusText = MUSIC_SCHEDULE_COPY.notReported;
  else if (phase === "sending") statusText = DEVICE_COMMAND_OPERATOR_COPY.sending;
  else if (lastResult === "successful") statusText = DEVICE_COMMAND_OPERATOR_COPY.successful;
  else if (lastResult === "unsuccessful") statusText = DEVICE_COMMAND_OPERATOR_COPY.unsuccessful;

  const pendingCopy =
    phase === "sending"
      ? pendingMusicScheduleCopy({
          requested: pending,
          confirmed: confirmedAtConfirmRef.current,
        })
      : null;

  const canConfirm = canConfirmMusicSchedule({
    commandsEnabled,
    confirmedAvailable: phase === "ready" && confirmed !== null,
    fieldIsTouched,
    isMutating: isMutating || externalBusy,
    disabled,
  });
  const showRetry = phase === "unavailable" || phase === "not-reported";
  const lockedClass = classNames({ [styles.commandLocked]: controlsLocked });

  return (
    <Form
      className={classNames({ [styles.formDisabled]: Boolean(disabled) })}
      requiredMark={false}
      layout="vertical"
      form={form}
      onValuesChange={onValuesChange}
      onFinish={handleSubmit}
      data-testid="music-scheduler-section"
    >
      <div className={styles.settingsHeadingContainer}>
        <div className="d-flex flex-column">
          <Text type="title" size="md">
            Music Scheduler
          </Text>
          {statusText ? <Text size="sm">{statusText}</Text> : null}
          {pendingCopy ? (
            <Text size="sm" className={styles.pendingCompare}>
              {pendingCopy}
            </Text>
          ) : null}
          {formError ? <Text size="sm">{formError}</Text> : null}
        </div>
        <div className="d-flex align-items-center">
          {showRetry ? (
            <ButtonSmall
              type="button"
              className={styles.confirmButton}
              onClick={handleRetry}
            >
              {REMOTE_SETTINGS_RETRY_LABEL}
            </ButtonSmall>
          ) : null}
          <ButtonSmall
            disabled={!canConfirm}
            type="submit"
            variant="disabled"
            className={styles.confirmButton}
          >
            Confirm
          </ButtonSmall>
        </div>
      </div>

      <ControlPanelActionRow
        borderBottom={false}
        className={lockedClass}
        leftComponent={<Text size="md">Music Scheduler</Text>}
        rightComponent={
          <Form.Item
            layout="horizontal"
            className={`d-flex justify-content-end ${styles.formItem}`}
            name="enabled"
            valuePropName="checked"
          >
            <BrandSwitch
              toggleText={{ on: "On", off: "Off" }}
              disabled={controlsLocked}
            />
          </Form.Item>
        }
      />

      {SLOTS.map(({ prefix, label }) => (
        <React.Fragment key={prefix}>
          <ControlPanelActionRow
            borderBottom={false}
            className={lockedClass}
            leftComponent={<Text size="md">{label}</Text>}
            rightComponent={
              <Form.Item
                label={<Text size="md">Channel ID</Text>}
                className={styles.formItem}
                layout="horizontal"
                name={`${prefix}ChannelId`}
                labelCol={{
                  style: { display: "flex", justifyContent: "space-between", flex: 1 },
                }}
              >
                <Input
                  size="small"
                  placeholder="Channel ID"
                  maxLength={CHANNEL_ID_MAX_LENGTH}
                  disabled={controlsLocked}
                />
              </Form.Item>
            }
          />
          <ControlPanelActionRow
            borderBottom={false}
            className={lockedClass}
            leftComponent={<div />}
            rightComponent={
              <Form.Item
                label={<Text size="md">Channel Name</Text>}
                className={styles.formItem}
                layout="horizontal"
                name={`${prefix}ChannelName`}
                labelCol={{
                  style: { display: "flex", justifyContent: "space-between", flex: 1 },
                }}
              >
                <Input
                  size="small"
                  placeholder="Channel name"
                  maxLength={CHANNEL_NAME_MAX_LENGTH}
                  disabled={controlsLocked}
                />
              </Form.Item>
            }
          />
          <ControlPanelActionRow
            borderBottom={prefix === "slot2"}
            className={lockedClass}
            leftComponent={<div />}
            rightComponent={
              <Form.Item
                label={<Text size="md">{label} Time</Text>}
                className={styles.formItem}
                layout="horizontal"
                name={`${prefix}Time`}
                labelCol={{
                  style: { display: "flex", justifyContent: "space-between", flex: 1 },
                }}
              >
                <TimePicker
                  className="settings-time-picker"
                  format="hh:mm A"
                  minuteStep={1}
                  style={{ width: "100%" }}
                  use12Hours
                  disabled={controlsLocked}
                  placeholder="Select Time"
                />
              </Form.Item>
            }
          />
        </React.Fragment>
      ))}
    </Form>
  );
};

export default MusicSchedulerSection;
